export interface RandomSource {
  nextInteger(lowerInclusive: number, upperExclusive: number): number;
  nextUnit(): number;
}

const fnvOffsetBasis = 0x81_1c_9d_c5;
const fnvPrime = 0x01_00_01_93;
const unitDivisor = 0x1_00_00_00_00;

const hashSeed = (seed: string): number => {
  let hash = fnvOffsetBasis;
  for (const value of new TextEncoder().encode(seed)) {
    hash = Math.imul(hash ^ value, fnvPrime) >>> 0;
  }
  return hash;
};

const validateIntegerRange = (
  lowerInclusive: number,
  upperExclusive: number,
): number => {
  if (
    !Number.isSafeInteger(lowerInclusive) ||
    !Number.isSafeInteger(upperExclusive) ||
    lowerInclusive >= upperExclusive
  ) {
    throw new RangeError("Random integer range must use ordered safe integers");
  }
  const size = upperExclusive - lowerInclusive;
  if (!Number.isSafeInteger(size)) {
    throw new RangeError("Random integer range size must be a safe integer");
  }
  return size;
};

export class SeededRandom implements RandomSource {
  private state: number;

  public constructor(seed: string) {
    this.state = hashSeed(seed);
  }

  public nextInteger(lowerInclusive: number, upperExclusive: number): number {
    const size = validateIntegerRange(lowerInclusive, upperExclusive);
    return lowerInclusive + Math.floor(this.nextUnit() * size);
  }

  public nextUnit(): number {
    let next = this.state;
    next ^= next << 13;
    next ^= next >>> 17;
    next ^= next << 5;
    this.state = next >>> 0;
    return this.state / unitDivisor;
  }
}
